import './loanDocumentMileagePage.scss';

import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useLoanDocumentStore from '../../../store/loanDocumentStore/useLoanDocumentStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { t } = useTranslation();

  const isSimplifiedWorkflowEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_SIMPLIFIED_LOAN_DOCUMENTS_WORKFLOW,
  );

  const {
    clearAllDocuments,
    isUploadFinished,
    isUploadSuccess,
    isUploading,
    mileage,
    setMileage,
    uploadDocuments,
    uploadFailedMessage,
  } = useLoanDocumentStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      mileageValue: mileage,
    },
    delayError: 200,
  });

  const leadingZeroValidation = (
    value: string,
    error: string,
  ): string | boolean => {
    if (value.length > 1 && value.startsWith('0')) {
      return error;
    }
  };

  const mileageValidation = {
    required: t('validation.mileage.required'),
    validate: (value: string): string | boolean =>
      leadingZeroValidation(value, t('validation.mileage.invalid')),
  };

  useEffect((): void => {
    if (isUploadFinished && isUploadSuccess) {
      navigateWithCallback('/loan-documents/success').then((): void => {
        clearAllDocuments();
      });
    }
  }, [isUploadFinished, isUploadSuccess]);

  const previousStep = (): void => {
    if (isSimplifiedWorkflowEnabled) {
      navigate('/loan-documents/document-upload');
      return;
    }

    navigate('/loan-documents/list');
  };

  const formSubmit = (values: FieldValues): Promise<void> => {
    const { mileageValue } = values;

    if (!mileageValue) {
      return;
    }

    setMileage(mileageValue);

    uploadDocuments();
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page loan-document-mileage-page">
        <Header progress={90} showDrawerToggle onBackClick={previousStep} />

        <main>
          <form
            data-testid={locator.pages.loanDocumentMileage.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.loanDocumentMileage.form.h1}>
                {t('LoanDocumentMileagePage.title')}
              </h1>
              <h2 data-testid={locator.pages.loanDocumentMileage.form.h2}>
                {t('LoanDocumentMileagePage.subtitle')}
              </h2>

              {!isUploading && !isEmpty(uploadFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.loanDocumentMileage.alert.error}
                    severity="error"
                  >
                    {uploadFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <NumberInput
                  {...register('mileageValue', mileageValidation)}
                  data-testid={
                    locator.pages.loanDocumentMileage.form.mileageInput
                  }
                  error={errors.mileageValue}
                  errorTestId={
                    locator.pages.loanDocumentMileage.form.mileageInputError
                  }
                  label={t('LoanDocumentMileagePage.mileage')}
                  min={0}
                  type="number"
                  autoFocus
                />
              </div>

              <Button
                data-testid={
                  locator.pages.loanDocumentMileage.form.submitButton
                }
                disabled={!isValid}
                loader={isUploading}
                loaderPosition="start"
                fullWidth
                submit
              >
                {t('LoanDocumentMileagePage.submit')}
              </Button>
            </div>
            <footer data-testid={locator.pages.loanDocumentMileage.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'LoanDocumentMileagePage';
